/**
 * FO-019 (batch-15) — Cursisten aanmelden bij externe opleider.
 *
 * Gedeelde bron voor de aanmeld-mailtemplate + placeholder-resolver. Hergebruik
 * van het factuur-footer-patroon (`facturen-factoring.ts`): `[token]`-vorm,
 * default-tekst als constante, pure resolver zonder Prisma/I/O. Wordt gedeeld
 * door de API (genereren + versturen) en de backoffice (preview + instellingen).
 *
 * Twee placeholder-groepen:
 *  - algemeen: cursusnaam, bedrijfsnaam, contactpersoon, factuuradres(mail),
 *    contact-mail + `[cursisten]` (invoegpunt voor de cursist-blokken).
 *  - per cursist: voorletters, voor-/achternaam, adres, postcode + woonplaats,
 *    telefoon, geboortedatum + plaats, e-mail.
 */

import { z } from 'zod'
import { idParam } from './id-validators'

// ─── Placeholders (voor de placeholder-hulp in de instellingen-UI) ────────────

/** Algemene placeholders (eenmalig in de body/onderwerp). */
export const AANMELD_PLACEHOLDERS_ALGEMEEN = [
  '[cursusnaam]',
  '[bedrijfsnaam]',
  '[contactpersoon]',
  '[factuuradres]',
  '[contact_email]',
  '[cursisten]',
] as const

/** Per-cursist placeholders (in het cursist-blok, herhaald per cursist). */
export const AANMELD_PLACEHOLDERS_CURSIST = [
  '[voorletters]',
  '[voornaam]',
  '[achternaam]',
  '[adres]',
  '[postcode_woonplaats]',
  '[telefoon]',
  '[geboortedatum]',
  '[geboorteplaats]',
  '[email]',
] as const

/** Leesbare uitleg per placeholder (instellingen-UI). */
export const AANMELD_PLACEHOLDER_LABELS: Record<string, string> = {
  '[cursusnaam]': 'Naam van de cursus/opleiding',
  '[bedrijfsnaam]': 'Naam van de aanmeldende BV',
  '[contactpersoon]': 'Contactpersoon (aanmeld-config per BV)',
  '[factuuradres]': 'Factuuradres / factuur-mailadres (per BV)',
  '[contact_email]': 'Contact-mailadres (per BV)',
  '[cursisten]': 'Lijst van cursist-blokken (automatisch ingevuld)',
  '[voorletters]': 'Voorletters (afgeleid uit voornaam, corrigeerbaar)',
  '[voornaam]': 'Voornaam',
  '[achternaam]': 'Achternaam',
  '[adres]': 'Straat + huisnummer',
  '[postcode_woonplaats]': 'Postcode + woonplaats',
  '[telefoon]': 'Telefoonnummer',
  '[geboortedatum]': 'Geboortedatum',
  '[geboorteplaats]': 'Geboorteplaats',
  '[email]': 'E-mailadres',
}

// ─── Default-template (INSTELBAAR; default = deze tekst) ──────────────────────
// Geen em-dash; merknaam wordt door [bedrijfsnaam] (de BV-naam) ingevuld.

export const DEFAULT_AANMELD_ONDERWERP = 'Aanmelding cursist(en) - [cursusnaam]'

export const DEFAULT_AANMELD_BODY = `Geachte heer/mevrouw,

Hierbij melden wij namens [bedrijfsnaam] de volgende cursist(en) aan voor de opleiding "[cursusnaam]".

[cursisten]

De facturatie kan plaatsvinden via [factuuradres]. Voor vragen over deze aanmelding kunt u contact opnemen met [contactpersoon] via [contact_email].

Met vriendelijke groet,
[contactpersoon]
[bedrijfsnaam]`

export const DEFAULT_AANMELD_CURSIST_BLOK = `[voorletters] [achternaam] ([voornaam])
Adres: [adres], [postcode_woonplaats]
Telefoon: [telefoon]
Geboortedatum: [geboortedatum] te [geboorteplaats]
E-mailadres: [email]`

// ─── Ingevulde placeholder-waarden ────────────────────────────────────────────

export interface AanmeldAlgemeenPlaceholders {
  cursusnaam: string
  bedrijfsnaam: string
  contactpersoon: string
  factuuradres: string
  contactEmail: string
}

export interface AanmeldCursistPlaceholders {
  voorletters: string
  voornaam: string
  achternaam: string
  adres: string
  postcodeWoonplaats: string
  telefoon: string
  geboortedatum: string
  geboorteplaats: string
  email: string
}

// ─── Resolver (pure; globale replace zodat een token meermaals mag voorkomen) ─

function resolveAlgemeen(template: string, a: AanmeldAlgemeenPlaceholders): string {
  return template
    .replace(/\[cursusnaam\]/g, a.cursusnaam)
    .replace(/\[bedrijfsnaam\]/g, a.bedrijfsnaam)
    .replace(/\[contactpersoon\]/g, a.contactpersoon)
    .replace(/\[factuuradres\]/g, a.factuuradres)
    .replace(/\[contact_email\]/g, a.contactEmail)
}

/** Vul één cursist-blok in. */
export function resolveCursistBlok(template: string, c: AanmeldCursistPlaceholders): string {
  return template
    .replace(/\[voorletters\]/g, c.voorletters)
    .replace(/\[voornaam\]/g, c.voornaam)
    .replace(/\[achternaam\]/g, c.achternaam)
    .replace(/\[adres\]/g, c.adres)
    .replace(/\[postcode_woonplaats\]/g, c.postcodeWoonplaats)
    .replace(/\[telefoon\]/g, c.telefoon)
    .replace(/\[geboortedatum\]/g, c.geboortedatum)
    .replace(/\[geboorteplaats\]/g, c.geboorteplaats)
    .replace(/\[email\]/g, c.email)
}

/** Vul het onderwerp in (alleen algemene placeholders). */
export function resolveAanmeldOnderwerp(
  template: string,
  algemeen: AanmeldAlgemeenPlaceholders,
): string {
  return resolveAlgemeen(template, algemeen)
}

/**
 * Bouw de volledige body: per cursist het blok invullen, samenvoegen op het
 * `[cursisten]`-invoegpunt, daarna de algemene placeholders invullen. De
 * cursist-blokken worden eerst ingevuld zodat hun (persoons)waarden nooit per
 * ongeluk als algemene token worden geïnterpreteerd.
 */
export function resolveAanmeldBody(opts: {
  bodySjabloon: string
  cursistBlokSjabloon: string
  algemeen: AanmeldAlgemeenPlaceholders
  cursisten: AanmeldCursistPlaceholders[]
}): string {
  const blokken = opts.cursisten
    .map((c) => resolveCursistBlok(opts.cursistBlokSjabloon, c).trim())
    .join('\n\n')
  const metCursisten = opts.bodySjabloon.replace(/\[cursisten\]/g, blokken)
  return resolveAlgemeen(metCursisten, opts.algemeen)
}

// ─── Voorletters (afgeleid uit voornaam, met handmatige correctie) ────────────

/**
 * Leidt voorletters af uit een voornaam: "Jan Pieter" → "J.P.",
 * "Anne-Marie" → "A.M.", "José" → "J.". Splitst op spaties en koppeltekens,
 * neemt de eerste letter van elk deel (uppercase) met punt. Handmatige correctie
 * blijft mogelijk in de UI (geen nieuw verplicht veld op de medewerker).
 */
export function afleidVoorletters(voornaam: string): string {
  const delen = (voornaam ?? '')
    .split(/[\s-]+/)
    .map((d) => d.trim())
    .filter((d) => d.length > 0)
  if (delen.length === 0) return ''
  return delen.map((d) => `${d[0].toUpperCase()}.`).join('')
}

// ─── Ontbrekende-velden-check (geen lege placeholders ongemerkt versturen) ────

export const AANMELD_CURSIST_VELD_LABELS: Record<keyof AanmeldCursistPlaceholders, string> = {
  voorletters: 'Voorletters',
  voornaam: 'Voornaam',
  achternaam: 'Achternaam',
  adres: 'Adres',
  postcodeWoonplaats: 'Postcode + woonplaats',
  telefoon: 'Telefoon',
  geboortedatum: 'Geboortedatum',
  geboorteplaats: 'Geboorteplaats',
  email: 'E-mailadres',
}

/** Labels van de lege (ontbrekende) cursist-velden, voor de waarschuwing. */
export function ontbrekendeCursistVelden(c: AanmeldCursistPlaceholders): string[] {
  return (Object.keys(AANMELD_CURSIST_VELD_LABELS) as Array<keyof AanmeldCursistPlaceholders>)
    .filter((k) => !c[k] || c[k].trim() === '')
    .map((k) => AANMELD_CURSIST_VELD_LABELS[k])
}

// ─── DTOs ──────────────────────────────────────────────────────────────────────

/** Per-BV aanmeld-config (bedrijfsnaam komt van de Entity zelf). */
export interface AanmeldConfigBvDTO {
  entityId: string
  entityNaam: string
  contactpersoon: string | null
  factuurEmail: string | null
  contactEmail: string | null
}

/** Globale aanmeld-template (override of de ingebouwde default). */
export interface AanmeldInstellingDTO {
  onderwerpSjabloon: string
  bodySjabloon: string
  cursistBlokSjabloon: string
  /** True zolang nog niet overschreven (toont de default-tekst). */
  isDefault: boolean
}

export interface AanmeldCursistPreviewDTO {
  employeeId: string
  naam: string
  /** Default afgeleide voorletters (corrigeerbaar in de UI). */
  voorletters: string
  /** Labels van de lege persoonsgegevens-velden van deze cursist. */
  ontbrekendeVelden: string[]
}

/** Resultaat van de preview-generatie (bewerkbaar vóór kopiëren/versturen). */
export interface AanmeldPreviewResultDTO {
  onderwerp: string
  body: string
  /** Vooringevuld = Opleider.contactEmail; aanpasbaar. Leeg als onbekend. */
  ontvanger: string
  /** Contact-mail (cc/afzender), uit de aanmeld-config per BV. */
  cc: string[]
  bedrijfsnaam: string
  cursisten: AanmeldCursistPreviewDTO[]
  /**
   * True als persoonsgegevens (adres, geboortedatum/-plaats, privé-contact)
   * gemaskeerd zijn omdat de actor geen `MEDEWERKERS.canEdit` heeft. Die velden
   * komen dan leeg terug en verschijnen in de ontbrekende-velden-waarschuwing.
   */
  privacyMasked: boolean
}

export interface OpleidingAanmeldingDeelnemerDTO {
  employeeId: string
  naamSnapshot: string
}

/** Audit-rij: één aanmelding op de sessie (wie/wanneer/methode/cursisten). */
export interface OpleidingAanmeldingDTO {
  id: string
  sessieId: string
  entityId: string | null
  entityNaam: string | null
  methode: 'VERSTUURD' | 'GEKOPIEERD'
  ontvangerEmail: string
  ccEmail: string | null
  onderwerp: string
  mailStatus: string | null
  aangemeldDoor: string
  aangemeldDoorNaam: string | null
  aangemeldOp: string
  deelnemers: OpleidingAanmeldingDeelnemerDTO[]
}

// ─── Zod schemas (gedeeld: API .safeParse + client-form .safeParse) ───────────

export const AanmeldMethodeSchema = z.enum(['VERSTUURD', 'GEKOPIEERD'])

const aanmeldEmail = z.string().trim().max(200).email('Ongeldig e-mailadres')
const aanmeldEmailLeegOfNull = aanmeldEmail.nullable().optional().or(z.literal(''))

/** Preview genereren: cursisten + BV + optionele ontvanger-override + voorletters-correcties. */
export const AanmeldPreviewSchema = z.object({
  employeeIds: z.array(z.string().min(1)).min(1, 'Kies minimaal één cursist').max(100),
  entityId: idParam('entityId'),
  ontvangerOverride: aanmeldEmail.optional(),
  /** Per employeeId een gecorrigeerde voorletters-waarde (default = afgeleid). */
  voorletters: z.record(z.string(), z.string().trim().max(20)).optional(),
})
export type AanmeldPreviewInput = z.infer<typeof AanmeldPreviewSchema>

/**
 * Versturen of (na kopiëren) registreren: de definitieve, bewerkte mail + methode.
 * Een geldig ontvanger-mailadres is alleen verplicht bij VERSTUURD; bij GEKOPIEERD
 * mag het leeg zijn (de gebruiker stelt de ontvanger in zijn eigen mail in).
 */
export const AanmeldVerstuurSchema = z
  .object({
    methode: AanmeldMethodeSchema,
    employeeIds: z.array(z.string().min(1)).min(1, 'Kies minimaal één cursist').max(100),
    entityId: idParam('entityId'),
    onderwerp: z.string().trim().min(1, 'Onderwerp is verplicht').max(300),
    body: z.string().trim().min(1, 'De mailtekst is verplicht').max(20000),
    ontvanger: z.string().trim().max(200).optional().default(''),
    cc: z.array(aanmeldEmail).max(10).optional(),
  })
  .superRefine((val, ctx) => {
    const ontvanger = val.ontvanger ?? ''
    const geldig = z.string().email().safeParse(ontvanger).success
    if (val.methode === 'VERSTUURD' && !geldig) {
      ctx.addIssue({
        code: 'custom',
        path: ['ontvanger'],
        message: 'Vul een geldig ontvanger-mailadres in om te versturen',
      })
    } else if (val.methode === 'GEKOPIEERD' && ontvanger !== '' && !geldig) {
      ctx.addIssue({ code: 'custom', path: ['ontvanger'], message: 'Ongeldig e-mailadres' })
    }
  })
export type AanmeldVerstuurInput = z.infer<typeof AanmeldVerstuurSchema>

/** Aanmeld-config per BV bewerken (lege string = wissen → null). */
export const AanmeldConfigBvSchema = z.object({
  contactpersoon: z.string().trim().max(200).nullable().optional().or(z.literal('')),
  factuurEmail: aanmeldEmailLeegOfNull,
  contactEmail: aanmeldEmailLeegOfNull,
})
export type AanmeldConfigBvInput = z.infer<typeof AanmeldConfigBvSchema>

/** Globale aanmeld-template bewerken. */
export const AanmeldInstellingSchema = z.object({
  onderwerpSjabloon: z.string().trim().min(1, 'Onderwerp-sjabloon is verplicht').max(500),
  bodySjabloon: z.string().trim().min(1, 'Body-sjabloon is verplicht').max(20000),
  cursistBlokSjabloon: z.string().trim().min(1, 'Cursist-blok-sjabloon is verplicht').max(5000),
})
export type AanmeldInstellingInput = z.infer<typeof AanmeldInstellingSchema>
